import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

// Roots of every query whose payload carries permissions/roles. The server
// pushes `permissions.updated` on the realtime socket whenever a membership,
// role, resource scope or the web flag changes, so these are revalidated on
// demand instead of on a polling interval.
export const PERMISSION_QUERY_ROOTS = new Set([
  "auth",
  "workspace-access",
  "tenant-operations",
  // Layer/form visibility also depends on the membership scope.
  "project",
]);

// `focus` and `visibilitychange` both fire when the user returns to the tab;
// the realtime push is the primary channel, so the focus fallback runs at
// most once a minute.
const FOCUS_REFRESH_THROTTLE_MS = 60_000;
// With a live socket, only a tab hidden this long (when browsers may throttle
// or drop the socket) is worth re-checking on return.
const MIN_HIDDEN_BEFORE_REFRESH_MS = 30_000;

/**
 * Keeps permission-bearing queries in sync with the server.
 *
 * Must be rendered inside the authenticated route tree (not at the app root):
 * `useRealtimeEvents` reads the auth token during render, and the token only
 * lands in storage after the async session restore / login. Rendering here
 * guarantees this component re-renders once the session exists, so the
 * realtime subscription is actually opened.
 */
export function PermissionSyncObserver() {
  const queryClient = useQueryClient();
  const lastFocusRefreshRef = useRef(0);
  const hiddenAtRef = useRef<number | null>(null);

  const refreshPermissions = useCallback(() => {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        PERMISSION_QUERY_ROOTS.has(String(query.queryKey[0])),
      refetchType: "active",
    });
  }, [queryClient]);

  const { connectionState } = useRealtimeEvents({
    onEvent: (payload) => {
      if (payload?.event !== "permissions.updated") return;
      refreshPermissions();
    },
  });
  const connectionStateRef = useRef(connectionState);
  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  // Fallback for clients whose socket is down (or may have missed pushes while
  // the tab was hidden for a while): re-check when the tab regains focus,
  // throttled. No periodic polling.
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== "visible") {
        hiddenAtRef.current ??= Date.now();
        return;
      }
      const now = Date.now();
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      const socketDown = connectionStateRef.current !== "connected";
      const wasHiddenLongEnough =
        hiddenAt !== null && now - hiddenAt >= MIN_HIDDEN_BEFORE_REFRESH_MS;
      if (!socketDown && !wasHiddenLongEnough) return;
      if (now - lastFocusRefreshRef.current < FOCUS_REFRESH_THROTTLE_MS) return;
      lastFocusRefreshRef.current = now;
      refreshPermissions();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);

    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [refreshPermissions]);

  return null;
}
